import React, { useEffect, useRef, useState } from 'react';
import { CTAButton } from './CTAButton';
import { LibraryPreviewSheet, type LibraryPreviewItem } from './LibraryPreviewSheet';
const libraryItems: Array<{
  module: string;
  title: string;
  benefit: string;
  img: string;
  pos: string;
  alt: string;
  previewImage: string | null;
  description: string;
  index: string[] | null;
}> = [{
  module: 'tracker',
  title: 'Tracker Premium',
  benefit: '✓ Visualizá tu progreso día a día',
  img: 'https://cdn.shopify.com/s/files/1/0730/1614/8145/files/v5gVLGm07qY3WP7hCr5Mp_430x.jpg?v=1780543245',
  pos: 'object-[center_18%]',
  alt: 'Tracker Premium H.E.R.',
  previewImage: 'https://cdn.magicpatterns.com/uploads/wUFbzMsAKZ8Pgeg2UUQJ92/image.png',
  description: 'Registrá cómo descansás, cómo te sentís y qué hábitos querés sostener para visualizar tu progreso durante las tres semanas.',
  index: null
}, {
  module: 'noche',
  title: 'Noche en Calma',
  benefit: '✓ Dormí mejor sin complicarte',
  img: 'https://cdn.shopify.com/s/files/1/0730/1614/8145/files/qushb3WzLl3BYpA9n1eL_430x.jpg?v=1780543244',
  pos: 'object-[center_42%]',
  alt: 'Noche en Calma',
  previewImage: 'https://cdn.magicpatterns.com/uploads/henmqr8Y7MpAJyADaYpERD/image.png',
  description: 'Un ritual guiado para las noches en que cuesta desconectar, pensado para bajar el estado de alerta antes de dormir.',
  index: ['Noche 1 — Preparar el descanso.', 'Noche 2 — Bajar el ritmo.', 'Noche 3 — Crear una señal de calma.', 'Noche 4 — Rutina nocturna.', 'Noche 5 — Bebidas funcionales.', 'Noche 6 — Ajustar el entorno.', 'Noche 7 — Consolidar el ritual.']
}, {
  module: 'movement',
  title: 'H.E.R. Movement',
  benefit: '✓ Rutinas cortas que sí podés sostener',
  img: 'https://cdn.shopify.com/s/files/1/0730/1614/8145/files/S8SNVUZHdzf8t092yIlAK_430x.jpg?v=1780543245',
  pos: 'object-[center_34%]',
  alt: 'H.E.R. Movement',
  previewImage: 'https://cdn.magicpatterns.com/uploads/cDPFvrVsrLi1iw4sHgGEG3/image.png',
  description: 'Rutinas cortas para hacer en casa, sin equipamiento, adaptadas a cada semana del protocolo.',
  index: null
}, {
  module: 'organizacion',
  title: 'Organización Inteligente',
  benefit: '✓ Compras y planificación sin estrés',
  img: 'https://cdn.shopify.com/s/files/1/0730/1614/8145/files/1I1NNttJTHz_ql3yYujuX_430x.jpg?v=1780543245',
  pos: 'object-[center_18%]',
  alt: 'Organización Inteligente',
  previewImage: 'https://cdn.magicpatterns.com/uploads/sZz8hN9SCWgscmhNv5YSCm/image.png',
  description: 'Qué comprar y cómo organizar la semana, con precios y productos que se consiguen en Argentina.',
  index: null
}, {
  module: 'recetario',
  title: 'Recetario H.E.R.',
  benefit: '✓ Comidas simples para 21 días',
  img: 'https://cdn.shopify.com/s/files/1/0730/1614/8145/files/sOWIBzOpm0inWncyzKYoM_430x.jpg?v=1780543245',
  pos: 'object-[center_30%]',
  alt: 'Recetario H.E.R.',
  previewImage: null,
  description: 'Organizá tus comidas con 63 recetas simples, pensadas para que comer mejor no dependa de improvisar todos los días.',
  index: null
}];

// Ritmo de escaparate premium: ~1 card cada 10s (rango pedido: 8-12s), no un carrusel publicitario acelerado.
const SECONDS_PER_CARD = 10;
// Tras soltar/tocar, el autoscroll retoma a los ~1.8s (rango pedido: 1.5-2s).
const RESUME_DELAY_MS = 1800;
// Umbral de arrastre: por debajo de esto, un pointerdown+up se interpreta como toque (abre preview), no como swipe.
const TAP_DISTANCE_PX = 8;
export function HeroSection() {
  const scrollToOffer = () => {
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const [activeItem, setActiveItem] = useState<LibraryPreviewItem | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  const halfWidthRef = useRef(0);
  const isPausedRef = useRef(false);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef<number | null>(null);
  const dragStartOffsetRef = useRef(0);
  const dragDistanceRef = useRef(0);
  const resumeTimeoutRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);
  const clearResumeTimer = () => {
    if (resumeTimeoutRef.current !== null) {
      window.clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
  };
  const scheduleResume = () => {
    clearResumeTimer();
    resumeTimeoutRef.current = window.setTimeout(() => {
      isPausedRef.current = false;
      resumeTimeoutRef.current = null;
    }, RESUME_DELAY_MS);
  };
  const openPreview = (item: typeof libraryItems[number]) => {
    isPausedRef.current = true;
    clearResumeTimer();
    setActiveItem(item);
  };
  const closePreview = () => {
    setActiveItem(null);
    // El carrusel retoma desde offsetRef.current tal como quedó (no se reinicia al principio).
    scheduleResume();
  };
  useEffect(() => {
    const track = trackRef.current;
    const container = containerRef.current;
    if (!track || !container) return;

    // El array está duplicado (ver render más abajo), así que la mitad del ancho total
    // del track equivale al ancho de una copia completa de las cards.
    const measure = () => {
      halfWidthRef.current = track.scrollWidth / 2;
    };
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(track);
    const applyTransform = () => {
      track.style.transform = `translate3d(${-offsetRef.current}px,0,0)`;
    };

    // Movimiento 100% controlado por JS (transform), no por scrollLeft nativo:
    // en iOS/Safari (y en varios WebViews de apps como Instagram) las escrituras
    // programáticas de scrollLeft sobre un contenedor con overflow-x se ignoran
    // o se pisan fuera de un gesto táctil activo — por eso el carrusel podía
    // andar en PC y quedar estático en el celular. translate3d no depende del
    // motor de scroll nativo, así que se comporta igual en todos lados.
    const step = (ts: number) => {
      if (lastTsRef.current === null) lastTsRef.current = ts;
      const delta = ts - lastTsRef.current;
      lastTsRef.current = ts;
      const halfWidth = halfWidthRef.current;
      if (halfWidth > 0) {
        if (!isPausedRef.current && !isDraggingRef.current) {
          const pxPerMs = halfWidth / (libraryItems.length * SECONDS_PER_CARD * 1000);
          offsetRef.current += pxPerMs * delta;
        }

        // Loop infinito: al cruzar la mitad reposicionamos sin salto visual porque
        // la segunda mitad del track es una copia idéntica de la primera.
        if (offsetRef.current >= halfWidth) offsetRef.current -= halfWidth;
        if (offsetRef.current < 0) offsetRef.current += halfWidth;
        applyTransform();
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    const handlePointerDown = (e: PointerEvent) => {
      isDraggingRef.current = true;
      isPausedRef.current = true;
      dragDistanceRef.current = 0;
      clearResumeTimer();
      dragStartXRef.current = e.clientX;
      dragStartOffsetRef.current = offsetRef.current;
      try {
        container.setPointerCapture(e.pointerId);
      } catch {}
    };
    const handlePointerMove = (e: PointerEvent) => {
      if (!isDraggingRef.current || dragStartXRef.current === null) return;
      const dx = e.clientX - dragStartXRef.current;
      dragDistanceRef.current = Math.abs(dx);
      offsetRef.current = dragStartOffsetRef.current - dx;
    };
    const endDrag = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      dragStartXRef.current = null;
      // Si el gesto fue prácticamente estático, no fue un swipe: fue un toque en la card,
      // así que dejamos que el click de la card (más abajo) decida si abre el preview.
      if (dragDistanceRef.current >= TAP_DISTANCE_PX) {
        scheduleResume();
      }
    };

    // pointerdown/move/up cubren mouse, touch y stylus en un mismo listener.
    container.addEventListener('pointerdown', handlePointerDown, {
      passive: true
    });
    container.addEventListener('pointermove', handlePointerMove, {
      passive: true
    });
    container.addEventListener('pointerup', endDrag, {
      passive: true
    });
    container.addEventListener('pointercancel', endDrag, {
      passive: true
    });
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      clearResumeTimer();
      resizeObserver.disconnect();
      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointermove', handlePointerMove);
      container.removeEventListener('pointerup', endDrag);
      container.removeEventListener('pointercancel', endDrag);
    };
  }, []);
  return <section className="bg-gradient-hero min-h-0 md:min-h-screen flex items-start pt-10 pb-12 md:pt-12 md:pb-16 relative overflow-hidden" data-id="element-156" data-mp="3lwp46t4o2br2">
      <div className="absolute left-[8%] md:left-[20%] top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gold/15 to-transparent" data-id="element-157" data-mp="339fyyzbrzmmx" />

      <div className="absolute right-[8%] md:right-[20%] top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gold/15 to-transparent" data-id="element-158" data-mp="2ybek6qldu9h9" />

      <div className="max-w-[1400px] mx-auto px-5 md:px-8 w-full relative z-10" data-id="element-159" data-mp="379qh1acue3i5">
        <div className="flex flex-col items-center text-center" data-id="element-160" data-mp="3m70peau6ut9n">

          {/* Promesa principal */}
          <h1 className="font-display font-semibold leading-[1.08] tracking-tight max-w-4xl text-[#F0E8D8]" data-id="element-161" data-mp="3h8flo1fq9rgg">
            <span className="block text-[40px] md:text-6xl" style={{
            fontSize: '34px'
          }} data-id="element-162" data-mp="3v042gi0x943r">
              Volvé a sentirte más liviana.
            </span>

            <span className="block italic text-gradient-gold text-[38px] md:text-6xl my-1" style={{
            fontSize: '32px'
          }} data-id="element-163" data-mp="8sdv02gnrorw">
              Descansá mejor y recuperá energía.
            </span>

            <span className="block text-[36px] md:text-5xl" style={{
            fontSize: '30px'
          }} data-id="element-164" data-mp="2qsd3g9jijfjc">
              en 21 días.
            </span>
          </h1>

          {/* Beneficios */}
          <p className="mt-7 uppercase tracking-[0.18em] text-[12px] md:text-[13px] text-[#C8A870] font-semibold leading-[2]" style={{
          paddingRight: '0px',
          width: '310px',
          height: '42px',
          fontWeight: '500',
          textAlign: 'center'
        }} data-id="element-165" data-mp="2hzrvzplc0v9t">
            ✦ Más energía&nbsp;&nbsp;&nbsp;✦ Menos hinchazón&nbsp; ✦ Hábitos
            simples
          </p>

          {/* Ebook principal */}
          <div className="relative mt-12 mb-8 w-full max-w-[19rem]" data-id="element-166" data-mp="30phmw1lz4wwg">
            <div className="absolute inset-0 -z-10 flex items-center justify-center pointer-events-none" data-id="element-167" data-mp="2vs7se7jq9v1r">
              <div className="w-[75%] h-[75%] rounded-full bg-gold/10 blur-[80px]" data-id="element-168" data-mp="1pbw0upbevbf5" />
            </div>

            <div className="relative text-center" data-id="element-169" data-mp="1kc9la9841j36">
              <img src="https://cdn.shopify.com/s/files/1/0730/1614/8145/files/ebook_430x.webp?v=1780543244" alt="Protocolo H.E.R." width="340" height="456" loading="eager" fetchPriority="high" decoding="async" className="w-full max-w-[340px] mx-auto block rounded-[4px] shadow-[0_25px_60px_rgba(0,0,0,0.45)]" data-id="element-170" data-mp="1kevwciu7mgq5" />
            </div>
          </div>

          {/* CTA principal: ahora aparece antes de la Biblioteca */}
          <div className="pt-2 flex flex-col items-center w-full max-w-md space-y-4" data-id="element-171" data-mp="1sz3v5tome8po">
            <CTAButton section="hero" onClick={scrollToOffer} data-id="element-172" data-mp="1t1gb61ati903">
              Acceder al Protocolo H.E.R.
            </CTAButton>

            <p className="uppercase tracking-[0.14em] md:text-[12px] font-body text-center text-[11px] text-[#C8A870]" data-id="element-173" data-mp="22wqin4jzj8l9">
              ✦ Acceso inmediato&nbsp;&nbsp; ✦ Garantía 30 días
            </p>
          </div>

          {/* Biblioteca H.E.R. */}
          <div className="w-full max-w-[500px] mx-auto mt-14 overflow-hidden" aria-label="Biblioteca H.E.R." data-id="element-174" data-mp="8lbp0cnusp08">
            <p className="text-center tracking-[0.22em] uppercase text-[#c9a96e] mb-3 text-[13px]" style={{
            fontSize: '15px'
          }} data-id="element-175" data-mp="dk6h657pnpx0">
              Biblioteca Digital H.E.R.
            </p>

            <h2 className="text-center font-serif m-0 text-[32px] leading-[1.08]" data-id="element-176" data-mp="fftr2umundwe">
              <em className="text-[#c9a96e] italic" data-id="element-177" data-mp="fdqs3l3pyafj">
                Todo lo que incluye H.E.R.
              </em>
            </h2>

            

            {/* Carrusel: posición 100% manejada por JS (translate3d), sin overflow-x
                nativo ni scrollLeft. Autoplay vía requestAnimationFrame, arrastre
                manual vía Pointer Events — mismo comportamiento en PC, Android e iOS.
                Un toque sin arrastre (< TAP_DISTANCE_PX) abre el preview de la card. */}
            <div ref={containerRef} className="relative w-full overflow-hidden mt-6" style={{
            touchAction: 'pan-y',
            cursor: 'grab'
          }} data-id="element-178" data-mp="t80447uph4rm">
                <div ref={trackRef} className="flex w-max gap-4 will-change-transform" aria-label="Recursos incluidos en la Biblioteca Digital H.E.R." data-id="element-179" data-mp="136pe7egd1kxb">
                {[...libraryItems, ...libraryItems].map((item, index) => <article key={`${item.module}-${index}`} role="button" tabIndex={0} aria-label={`Ver qué incluye ${item.title}`} onClick={() => {
                if (dragDistanceRef.current < TAP_DISTANCE_PX) openPreview(item);
              }} onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openPreview(item);
                }
              }} className="shrink-0 w-[72vw] max-w-[360px] border border-[#c9a96e]/20 rounded-[18px] bg-[#141210] overflow-hidden cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-gold" data-id="element-180" data-mp="1qs9icny0d04w" data-mpi={index}>
                    <div className="h-[220px] overflow-hidden bg-[#090807]" data-id="element-181" data-mp="y1ridvskzevp">
                      <img src={item.img} alt={item.alt} loading="lazy" decoding="async" draggable={false} className={`w-full h-full object-cover pointer-events-none ${item.pos}`} data-id="element-182" data-mp="k54l1rwpump9" />
                    </div>

                    <div className="px-[18px] py-4" style={{
                  minHeight: '68px'
                }} data-id="element-183" data-mp="1rg2e9lou7hwt">
                      <h3 className="sr-only" data-id="element-184" data-mp="1nhmj2uilr38b">{item.title}</h3>

                      <p className="mt-1 text-center text-[15px] leading-snug text-[#c9a96e] font-medium" style={{
                    fontSize: '17px'
                  }} data-id="element-185" data-mp="1imz01b6db114">
                        {item.benefit}
                      </p>
                    </div>
                  </article>)}
              </div>
            <p className="mt-2 text-center font-body text-[12px] uppercase tracking-[0.14em] text-[#c9a96e]/60" style={{
              fontWeight: "500"
            }} data-id="element-186" data-mp="13py6r38j2y0j">
              Tocá una card para ver qué incluye
            </p></div>
          </div>

          {/* Testimonio */}
          <div className="mt-14 w-full max-w-xl" data-id="element-187" data-mp="2u2dtw63a5cs0">
            
          </div>

        </div>
      </div>

      <LibraryPreviewSheet item={activeItem} onClose={closePreview} data-id="element-188" data-mp="2p55bc4e7z6q8" />
    </section>;
}